import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, ExternalLink, QrCode, ShieldCheck } from "lucide-react";
import { publicLandingUrl } from "./_shared";

/* ---------------------------------------------------------------------------
 * QR block — SVG with download button.
 * ------------------------------------------------------------------------- */
export function QRBlock({ slug }) {
  const wrapRef = useRef(null);
  if (!slug) return null;
  // Bare-slug URL (e.g. /[slug]) — always uses the CUSTOMER-FACING public
  // origin (REACT_APP_PUBLIC_URL, e.g. https://play.amblemead.biz), never
  // the admin URL the super-admin is currently on. This keeps the QR
  // codes + share links stable regardless of which environment the admin
  // uses (dugsy-predictions.emergent.host, preview, custom domain, etc.).
  const url = publicLandingUrl(slug);

  const download = () => {
    const svg = wrapRef.current?.querySelector("svg");
    if (!svg) return;
    const source = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${slug}-qr.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const copy = () => {
    navigator.clipboard?.writeText(url);
    toast.success(`Copied ${url}`);
  };

  return (
    <div className="flex items-start gap-4" data-testid="qr-block">
      <div ref={wrapRef} className="p-2 border border-zinc-200 rounded-md bg-white">
        <QRCodeSVG value={url} size={128} level="M" includeMargin={false} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">QR Code · /{slug}</p>
        <p className="text-xs text-zinc-600 mt-1 break-all">{url}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={download}
            data-testid="qr-download-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8"
          >
            <QrCode className="h-3.5 w-3.5" /> Download SVG
          </button>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8"
          >
            <Copy className="h-3.5 w-3.5" /> Copy URL
          </button>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            data-testid={`qr-open-landing-${slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open landing
          </a>
          <a
            href={`/c/${slug}/editor`}
            target="_blank"
            rel="noreferrer"
            data-testid={`manage-back-office-${slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-zinc-950 hover:bg-black rounded-sm px-3 h-8"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Manage back office
          </a>
        </div>
      </div>
    </div>
  );
}
